import { ApiError, apiClient } from "@/lib/api-client";
import type { ImportCartResponse } from "@/types/api";

export interface ServerCartItem {
  productId: string;
  name: string;
  article: string;
  image: string;
  priceRetail: number;
  priceWholesale: number;
  discount: number;
  quantity: number;
  unit: string;
  minOrder: number;
  quantityStep?: number;
  stock: number;
}

export interface CartResponse {
  items: ServerCartItem[];
}

/**
 * POST /cart/import, or — on a backend that doesn't have it yet (404: api/php is deployed
 * by hand, after the frontend) — one POST /cart/items per line, one after another so they
 * can't overwrite each other, then the resulting cart. Remove the fallback once every
 * backend serves /cart/import.
 */
async function importItems(
  items: { productId: string; quantity: number }[],
): Promise<ImportCartResponse> {
  try {
    return await apiClient.post<ImportCartResponse>("/cart/import", { items });
  } catch (error) {
    if (!(error instanceof ApiError && error.status === 404)) throw error;
    for (const i of items) await cartApi.addItem(i.productId, i.quantity);
    const cart = await cartApi.getCart();
    return { items: cart.items, rejected: [] };
  }
}

export const cartApi = {
  getCart: () => apiClient.get<CartResponse>("/cart"),
  addItem: (productId: string, quantity: number) =>
    apiClient.post<CartResponse>("/cart/items", { productId, quantity }),
  updateItem: (productId: string, quantity: number) =>
    apiClient.put<CartResponse>(`/cart/items/${productId}`, { quantity }),
  removeItem: (productId: string) => apiClient.del<void>(`/cart/items/${productId}`),
  clearCart: () => apiClient.del<void>("/cart"),
  /** One request for many lines; the server loads and saves the basket once. */
  importItems,
};
